import { useState } from "react";
import ScrollReveal from "@/components/ScrollReveal";
import { Droplet, Shield, Snowflake, Package, Hand, Sparkles } from "lucide-react";

const STEPS = [
  { id: 1, icon: Droplet,    title: "Collection",   summary: "Ethically sourced placental tissue from healthy, consented donors.",
    detail: "Donors undergo rigorous medical and social-history screening, plus FDA-mandated infectious disease testing prior to recovery." },
  { id: 2, icon: Shield,     title: "Preservation", summary: "Aseptic processing preserves the native extracellular matrix.",
    detail: "Proprietary aseptic technique retains endogenous proteins, growth factors, and ECM architecture critical to wound healing." },
  { id: 3, icon: Snowflake,  title: "Lyophilization", summary: "Sublimation removes water without disrupting biological integrity.",
    detail: "Vacuum-driven freeze-drying produces a dry, stable allograft — eliminating cold-chain dependencies while preserving structure." },
  { id: 4, icon: Package,    title: "Storage",      summary: "Up to 24 months at ambient temperature.",
    detail: "Drop-in shelf storage. No freezer. No rehydration. No expiration drama." },
  { id: 5, icon: Hand,       title: "Application",  summary: "Direct or BCL placement — flexible, easy to handle.",
    detail: "Compatible with in-office or surgical workflows. The membrane conforms to the ocular surface immediately." },
  { id: 6, icon: Sparkles,   title: "Healing",      summary: "Creates a healing environment for ocular surface regeneration.",
    detail: "Provides a natural protective barrier and may support reepithelialization across a range of indications." },
];

export default function Lyophilization() {
  const [active, setActive] = useState(3);
  const ActiveIcon = STEPS[active - 1].icon;

  return (
    <section data-testid="lyophilization-section" className="relative bg-slate1 text-white py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute -top-40 right-1/4 w-[500px] h-[500px] rounded-full bg-radial-glow opacity-50 blur-2xl" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
            <div className="lg:col-span-8">
              <div className="text-xs tracking-[0.22em] uppercase text-teal font-semibold">
                Flagship Science
              </div>
              <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl leading-tight font-medium">
                The power of <span className="text-gradient-teal-bright">lyophilization</span>.
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-white/65 text-lg leading-relaxed">
                Six steps that transform regenerative biology into a stable,
                shelf-ready, clinician-friendly allograft.
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting line */}
          <div className="absolute left-0 right-0 top-7 h-px bg-gradient-to-r from-transparent via-teal/40 to-transparent hidden md:block" />

          <div className="grid grid-cols-2 md:grid-cols-6 gap-y-10 md:gap-x-6 relative">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const isActive = active === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  data-testid={`lyo-step-${s.id}`}
                  className="group flex flex-col items-center text-center px-2"
                >
                  <div
                    className={`relative h-14 w-14 rounded-full flex items-center justify-center transition-all duration-500 ${
                      isActive
                        ? "bg-teal text-white scale-110 shadow-[0_0_0_8px_rgba(21,184,184,0.18)]"
                        : "bg-white/5 border border-white/15 text-white/70 hover:border-teal/60 hover:text-teal"
                    }`}
                  >
                    <Icon className="h-6 w-6" />
                    {isActive && <span className="absolute inset-0 rounded-full animate-pulse-ring border-2 border-teal" />}
                  </div>
                  <div className={`mt-4 text-[10px] tracking-[0.2em] uppercase font-bold ${isActive ? "text-teal2" : "text-white/40"}`}>
                    Step {s.id.toString().padStart(2, "0")}
                  </div>
                  <div className={`mt-2 font-display text-base md:text-lg font-medium ${isActive ? "text-white" : "text-white/65"} transition-colors`}>
                    {s.title}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        <ScrollReveal>
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid={`lyo-detail-${active}`}>
            <div className="lg:col-span-5">
              <div className="relative aspect-square max-w-md mx-auto">
                <div className="absolute inset-0 bg-radial-glow rounded-full" />
                <div className="relative h-full w-full rounded-3xl glass-dark flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 grid-bg opacity-30" />
                  <ActiveIcon className="h-32 w-32 text-teal2" strokeWidth={1.2} />
                  <div className="absolute bottom-6 left-6 font-display text-7xl font-medium text-white/8 select-none">
                    0{active}
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="text-xs tracking-[0.22em] uppercase text-teal2 font-semibold">
                Step 0{active}
              </div>
              <h3 className="mt-3 font-display text-3xl md:text-4xl font-medium">{STEPS[active - 1].title}</h3>
              <p className="mt-5 text-xl text-white/80 leading-relaxed">{STEPS[active - 1].summary}</p>
              <p className="mt-5 text-white/55 leading-relaxed">{STEPS[active - 1].detail}</p>
              <div className="mt-8 flex gap-3 flex-wrap">
                {["Room-temperature storage", "Reduced waste", "Preserved ECM", "Simplified logistics"].map((t) => (
                  <span key={t} className="rounded-full bg-white/5 border border-teal/20 px-4 py-2 text-xs text-white/85">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
